/** What the delivery views need beyond reads: POST, a binary download, saving a file (keys are the shared `IntentKeys`). */
import { decodeApiError, type ApiErrorBody } from "../../contracts/envelope.ts";
import type { ViewContext, ViewReader } from "../../app/view.ts";

export interface DownloadAnswer {
  readonly status: number;
  readonly ok: boolean;
  readonly bytes: Uint8Array;
  readonly sha256: string | null;
  readonly chunk: number | null;
  readonly chunks: number | null;
  readonly error: ApiErrorBody | null;
}

export interface DeliveryServices {
  post<T>(path: string, body: unknown, decode: (json: unknown) => T): Promise<T | null>;
  download(path: string): Promise<DownloadAnswer>;
  save(filename: string, bytes: Uint8Array): void;
}

interface Poster extends ViewReader {
  post<T>(path: string, body: unknown, decode: (json: unknown) => T): Promise<T | null>;
}

function canPost(client: ViewReader): client is Poster {
  return "post" in client && typeof client.post === "function";
}

function headerNumber(response: Response, name: string): number | null {
  const text = response.headers.get(name);
  return text !== null && /^\d{1,9}$/.test(text) ? Number(text) : null;
}

async function refusal(response: Response): Promise<ApiErrorBody | null> {
  try {
    return decodeApiError(await response.json());
  } catch {
    return null;
  }
}

/** Build the services from a view context (its API client must also POST) and the window. */
export function servicesOf(ctx: ViewContext, win: Window): DeliveryServices {
  return {
    post: (path, body, decode) => {
      if (!canPost(ctx.client)) {
        return Promise.reject(new Error("the delivery views need the full API client"));
      }
      return ctx.client.post(path, body, decode);
    },
    download: async (path) => {
      const response = await win.fetch(path, {
        method: "GET",
        credentials: "same-origin",
        cache: "no-store",
        headers: { Accept: "text/csv" },
      });
      return {
        status: response.status,
        ok: response.ok,
        bytes: response.ok ? new Uint8Array(await response.arrayBuffer()) : new Uint8Array(),
        sha256: response.headers.get("X-Content-SHA256"),
        chunk: headerNumber(response, "X-Job-Chunk"),
        chunks: headerNumber(response, "X-Job-Chunks"),
        error: response.ok ? null : await refusal(response),
      };
    },
    save: (filename, bytes) => {
      const url = URL.createObjectURL(
        new Blob([bytes as Uint8Array<ArrayBuffer>], { type: "text/csv" }),
      );
      const link = win.document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    },
  };
}
